/**
 * How the portal draws a Markdown document, element by element. Two surfaces
 * read this: `PortalMarkdown`, which renders the text for creators, and the
 * brief editor, which shows staff the same document as they write it. They
 * share the rules so what is written is what is read.
 *
 * Every size is a type role (`text-nf-ds-<role>` carries size, weight,
 * leading and tracking together): a brief's `#` is a section, its `##` a
 * title, its `###` the strong body weight, and everything else is body. A
 * `strong` keeps its parent's size and only takes the weight, so emphasis
 * inside a heading does not shrink it.
 *
 * `PORTAL_MARKDOWN_CONTENT` is the same table as descendant rules for a live
 * DOM (the editor). Tailwind only sees class names written out in full, so
 * it is spelled rather than derived, and `portalMarkdownStyles.test.ts` holds
 * the two in step.
 */
export const PORTAL_MARKDOWN_ELEMENTS = {
  h1: "font-nf-ds-sans text-nf-ds-section mt-nf-ds-6 mb-nf-ds-2 first:mt-0",
  h2: "font-nf-ds-sans text-nf-ds-title mt-nf-ds-5 mb-nf-ds-2 first:mt-0",
  h3: "font-nf-ds-sans text-nf-ds-strong mt-nf-ds-4 mb-nf-ds-1 first:mt-0",
  p: "font-nf-ds-sans text-nf-ds-body text-foreground mb-nf-ds-3",
  ul: "list-disc pl-nf-ds-5 mb-nf-ds-3 space-y-nf-ds-1 font-nf-ds-sans text-nf-ds-body",
  ol: "list-decimal pl-nf-ds-5 mb-nf-ds-3 space-y-nf-ds-1 font-nf-ds-sans text-nf-ds-body",
  strong: "font-semibold",
  blockquote:
    "border-l-2 border-nf-ds-brand/40 pl-nf-ds-4 my-nf-ds-3 text-muted-foreground",
  code: "font-nf-ds-mono text-nf-ds-hint bg-nf-ds-bg-surface-2 rounded-nf-ds-sm px-nf-ds-1",
  a: "text-nf-ds-brand underline underline-offset-2 hover:opacity-80",
} as const;

export const PORTAL_MARKDOWN_CONTENT = [
  "[&_h1]:font-nf-ds-sans [&_h1]:text-nf-ds-section [&_h1]:mt-nf-ds-6 [&_h1]:mb-nf-ds-2 [&_h1]:first:mt-0",
  "[&_h2]:font-nf-ds-sans [&_h2]:text-nf-ds-title [&_h2]:mt-nf-ds-5 [&_h2]:mb-nf-ds-2 [&_h2]:first:mt-0",
  "[&_h3]:font-nf-ds-sans [&_h3]:text-nf-ds-strong [&_h3]:mt-nf-ds-4 [&_h3]:mb-nf-ds-1 [&_h3]:first:mt-0",
  "[&_p]:font-nf-ds-sans [&_p]:text-nf-ds-body [&_p]:text-foreground [&_p]:mb-nf-ds-3",
  "[&_ul]:list-disc [&_ul]:pl-nf-ds-5 [&_ul]:mb-nf-ds-3 [&_ul]:space-y-nf-ds-1 [&_ul]:font-nf-ds-sans [&_ul]:text-nf-ds-body",
  "[&_ol]:list-decimal [&_ol]:pl-nf-ds-5 [&_ol]:mb-nf-ds-3 [&_ol]:space-y-nf-ds-1 [&_ol]:font-nf-ds-sans [&_ol]:text-nf-ds-body",
  "[&_strong]:font-semibold",
  "[&_blockquote]:border-l-2 [&_blockquote]:border-nf-ds-brand/40 [&_blockquote]:pl-nf-ds-4 [&_blockquote]:my-nf-ds-3 [&_blockquote]:text-muted-foreground",
  "[&_code]:font-nf-ds-mono [&_code]:text-nf-ds-hint [&_code]:bg-nf-ds-bg-surface-2 [&_code]:rounded-nf-ds-sm [&_code]:px-nf-ds-1",
  "[&_a]:text-nf-ds-brand [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:opacity-80",
].join(" ");
